import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Empty, StatusPill } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function CalendarPage() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    const month = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
    api.get("/calendar", { params: { month } }).then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
  }, [cursor]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = Array.from({ length: offset }, () => null);
    for (let i = 1; i <= days; i++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((m) => {
      const k = (m.scheduled_at || "").slice(0, 10);
      (map[k] = map[k] || []).push(m);
    });
    return map;
  }, [items]);

  const todayKey = new Date().toISOString().slice(0, 10);
  const selectedItems = byDay[selected] || [];

  return (
    <div className="space-y-7" data-testid="calendar-page">
      <div>
        <p className="label-xs">Jadwal</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Meeting Calendar</h1>
        <p className="mt-2 text-sm text-slate-500">{items.length} rapat pada {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.7fr_1fr]">
        <div className="clay p-6" data-testid="calendar-grid-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl font-semibold text-slate-900">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-xl border-slate-200" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="calendar-prev-btn"><ChevronLeft size={16} /></Button>
              <Button variant="outline" size="sm" className="rounded-xl border-slate-200" onClick={() => setCursor(new Date())} data-testid="calendar-today-btn">Hari ini</Button>
              <Button variant="outline" size="sm" className="rounded-xl border-slate-200" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="calendar-next-btn"><ChevronRight size={16} /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {DAYS.map((d) => <div key={d} className="text-center label-xs py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {grid.map((d, i) => {
              if (!d) return <div key={i} />;
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              const list = byDay[key] || [];
              const isToday = key === todayKey;
              const isSel = key === selected;
              return (
                <button key={i} onClick={() => setSelected(key)} data-testid={`calendar-day-${key}`}
                  className={`min-h-[76px] rounded-xl border p-2 text-left transition-colors ${
                    isSel ? "border-brand-600 bg-brand-50/70" : isToday ? "border-cyanx/60 bg-cyan-50/40" : "border-slate-200/60 hover:bg-slate-50"}`}>
                  <span className={`text-xs font-semibold ${isToday ? "text-cyanx" : "text-slate-600"}`}>{d.getDate()}</span>
                  <div className="mt-1.5 space-y-1">
                    {list.slice(0, 2).map((m) => (
                      <span key={m.id} className="block truncate rounded grad-brand px-1.5 py-0.5 text-[10px] font-medium text-white">{m.title}</span>
                    ))}
                    {list.length > 2 && <span className="block text-[10px] text-slate-500">+{list.length - 2} lagi</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="clay p-6" data-testid="calendar-detail-card">
          <p className="label-xs">Agenda {selected}</p>
          {selectedItems.length === 0 ? (
            <Empty icon={CalendarDays} title="Tidak ada rapat" sub="Pilih tanggal lain atau buat rapat baru." />
          ) : (
            <div className="mt-5 space-y-3">
              {selectedItems.map((m) => (
                <Link key={m.id} to={`/meetings/${m.id}`} className="block rounded-xl border border-slate-200/70 p-4 hover:border-brand-600 hover:bg-brand-50/40 transition-colors" data-testid={`calendar-event-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-800">{m.title}</p>
                    <StatusPill status={m.status} />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    {new Date(m.scheduled_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} · {m.location || "—"} · {m.duration_minutes} menit
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
